import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, UsersThree, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["admin", "sales", "dispatcher", "driver"];
const ROLE_LABELS = { admin: "Admin", sales: "Sales Agent", dispatcher: "Dispatcher", driver: "Driver", carrier: "Owner-Carrier" };
const TABS = [["all", "All Users", null], ["employees", "Employees", ["admin", "sales", "dispatcher"]], ["drivers", "Drivers", ["driver"]], ["carriers", "Owner-Carriers", ["carrier"]]];
const ROLE_STYLES = { admin: "bg-blue-50 text-blue-700 ring-blue-200", sales: "bg-violet-50 text-violet-700 ring-violet-200", dispatcher: "bg-amber-50 text-amber-700 ring-amber-200", driver: "bg-emerald-50 text-emerald-700 ring-emerald-200", carrier: "bg-sky-50 text-sky-700 ring-sky-200" };
const empty = { name: "", email: "", password: "", role: "sales", phone: "", commission_rate: 0, monthly_target: 0, otp_email: "" };

export default function Team() {
  const { user } = useAuth();
  const invalidate = useInvalidate();
  const { data: users = [], isLoading } = useList("users", "/users");
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [linkDriver, setLinkDriver] = useState("none");
  const [tab, setTab] = useState("all");
  const tabRoles = TABS.find((t) => t[0] === tab)[2];
  const shown = tabRoles ? users.filter((u) => tabRoles.includes(u.role)) : users;

  const openNew = () => { setForm(empty); setLinkDriver("none"); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: "", role: u.role, phone: u.phone || "", commission_rate: u.commission_rate || 0, monthly_target: u.monthly_target || 0, otp_email: u.otp_email || "" });
    const roster = drivers.find((d) => d.user_id === u.id);
    setLinkDriver(roster ? roster.id : "none");
    setEditing(u.id); setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone, otp_email: form.otp_email.trim(), commission_rate: Number(form.commission_rate) || 0, monthly_target: Number(form.monthly_target) || 0 };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing}`, payload);
        if (form.role === "driver" && linkDriver !== "none") {
          try { await api.put(`/drivers/${linkDriver}`, { user_id: editing }); } catch (e) { console.error("Failed to link driver to user", e); }
        }
      } else {
        const { data: created } = await api.post("/users", { ...form, otp_email: form.otp_email.trim() || null, commission_rate: Number(form.commission_rate) || 0, monthly_target: Number(form.monthly_target) || 0 });
        if (form.role === "driver" && linkDriver !== "none") {
          try { await api.put(`/drivers/${linkDriver}`, { user_id: created.id }); } catch (e) { console.error("Failed to link driver to user", e); }
        }
      }
      toast.success(editing ? "Member updated" : "Member added");
      setOpen(false);
      invalidate(["users", "drivers"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); invalidate("users"); };
  const remove = async (id) => { if (!window.confirm("Remove this team member?")) return; try { await api.delete(`/users/${id}`); invalidate("users"); toast.success("Removed"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Team (Users)" subtitle="Employees, drivers and owner-carriers with access to Asterism"
        actions={<Button data-testid="new-member-btn" onClick={openNew} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold"><Plus size={18} weight="bold" className="mr-1.5" />Add Member</Button>} />

      <div className="flex flex-wrap gap-1 p-1 mb-4 rounded-xl bg-slate-100 w-fit" data-testid="team-tabs">
        {TABS.map(([k, label, roles]) => {
          const count = roles ? users.filter((u) => roles.includes(u.role)).length : users.length;
          return <button key={k} onClick={() => setTab(k)} data-testid={`team-tab-${k}`} className={`h-9 px-4 rounded-lg text-sm font-semibold transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{label} <span className="font-mono text-xs text-slate-400 ml-1">{count}</span></button>;
        })}
      </div>
      <Panel>
        {isLoading ? <div className="p-6 text-slate-500">Loading…</div> : users.length === 0 ? (
          <EmptyState icon={UsersThree} title="No Team Members" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="border-slate-200 hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {shown.map((u) => (
                  <TableRow key={u.id} className="border-slate-200 hover:bg-slate-50" data-testid={`member-row-${u.id}`}>
                    <TableCell className="text-slate-900 font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                    <TableCell><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset ${ROLE_STYLES[u.role] || "bg-slate-100 text-slate-700 ring-slate-200"}`} data-testid={`member-role-${u.id}`}>{ROLE_LABELS[u.role] || u.role}</span></TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-500">{u.phone || "—"}</TableCell>
                    <TableCell>
                      <button onClick={() => toggleActive(u)} disabled={u.id === user.id} data-testid={`toggle-active-${u.id}`}>
                        <StatusBadge status={u.active === false ? "lost" : "approved"} className="cursor-pointer">{u.active === false ? "Disabled" : "Active"}</StatusBadge>
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openEdit(u)} className="p-1.5 text-slate-500 hover:text-blue-600" data-testid={`edit-member-${u.id}`}><PencilSimple size={16} /></button>
                        {u.id !== user.id && <button onClick={() => remove(u.id)} className="p-1.5 text-slate-500 hover:text-red-400" data-testid={`delete-member-${u.id}`}><Trash size={16} /></button>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Member" : "Add Team Member"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Full Name *"><Input data-testid="member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-slate-300" /></Field>
            <Field label="Email *"><Input data-testid="member-email" type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-slate-300 disabled:opacity-60" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="member-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-slate-300" /></Field>
            </div>
            {form.role === "driver" && (
              <Field label="Link to Driver Roster (optional)">
                <Select value={linkDriver} onValueChange={setLinkDriver}>
                  <SelectTrigger className="bg-white border-slate-300" data-testid="member-link-driver"><SelectValue placeholder="Not Linked" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not Linked</SelectItem>{drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">Links this login to a roster record so the driver sees only their assigned loads.</p>
              </Field>
            )}
            {form.role === "sales" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Field label="Backup Email for Login Codes (used if the main email can't receive)"><Input type="email" data-testid="member-otp-email" value={form.otp_email} onChange={(e) => setForm({ ...form, otp_email: e.target.value })} placeholder="e.g. personal@gmail.com" className="bg-white border-slate-300" /></Field></div>
                <Field label="Commission Rate (%)"><Input type="number" data-testid="member-commission" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="bg-white border-slate-300 font-mono" /></Field>
                <Field label="Monthly Target ($)"><Input type="number" data-testid="member-target" value={form.monthly_target} onChange={(e) => setForm({ ...form, monthly_target: e.target.value })} className="bg-white border-slate-300 font-mono" /></Field>
              </div>
            )}
            <Field label={editing ? "New Password (leave blank to keep)" : "Password *"}><Input data-testid="member-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-slate-300" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">Cancel</Button>
            <Button data-testid="save-member-btn" onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">{saving ? "Saving…" : "Save Member"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
